/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { token } from '@atlaskit/tokens';
import type { Agent } from '@atlassian/conversation-assistant-service';

import type { LoadingStatus } from '../../common/ui/loading-status-indicator';

import { ExpandableLoadingStep } from './expandable-loading-step';
import { LoadingStep } from './loading-step';
import { RovoThinkingIndicator } from './thinking-indicator';

const isForgeAgent = (agent: Agent) =>
	agent.creator_type === 'THIRD_PARTY' || agent.creator_type === 'FORGE';

export type ExpandableTrace = {
	title: string;
	status: LoadingStatus;
	content?: React.ReactNode;
	defaultExpanded?: boolean;
};

type MessageLoadingProps = {
	thinkingText?: string;
	// TODO: adjust the type for traces and handle a more complex state
	traces: ExpandableTrace[];
	agent?: Agent;
	// allow explicit control of thinking indicator visibility
	hideThinkingIndicator?: boolean;
	disableAnimation?: boolean;
};

const styles = cssMap({
	container: {},
	stepContainerWithIndicator: { marginLeft: token('space.500') },
	stepContainerWithoutIndicator: { marginLeft: token('space.100') },
});

const ExpandableTrace = ({
	trace,
	isLastItem,
	placeholder = false,
}: {
	isLastItem: boolean;
	trace: ExpandableTrace;
	placeholder?: boolean;
}) => {
	const { title, defaultExpanded = false, content = null, status } = trace;

	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const setIsExpandedValue = useCallback(() => {
		setIsExpanded((prev) => !prev);
	}, [setIsExpanded]);

	const commonProps = { title, isLastItem, status };

	return content ? (
		<ExpandableLoadingStep isExpanded={isExpanded} onClick={setIsExpandedValue} {...commonProps}>
			{content}
		</ExpandableLoadingStep>
	) : (
		<LoadingStep {...commonProps} placeholder={placeholder} />
	);
};

export const MessageLoading = ({
	traces,
	disableAnimation,
	agent,
	hideThinkingIndicator = false,
	...props
}: MessageLoadingProps) => {
	const thinkingText = props.thinkingText ?? '';

	const agentAvatarOrUndefined = agent ? (
		<AgentAvatar
			agentNamedId={agent.external_config_reference ?? agent.named_id}
			agentId={agent.id}
			agentIdentityAccountId={agent.identity_account_id}
			size="small"
			showBorder={false}
			isForgeAgent={isForgeAgent(agent)}
			forgeAgentIconUrl={agent.icon}
		/>
	) : undefined;

	const needsPlaceholder = traces?.some((t) => t.content);
	const showThinkingIndicator = props.thinkingText && !hideThinkingIndicator;

	return (
		<Box xcss={styles.container}>
			{showThinkingIndicator ? (
				<RovoThinkingIndicator
					title={thinkingText}
					isAnimated={!disableAnimation}
					icon={agentAvatarOrUndefined}
				/>
			) : null}
			<Box
				xcss={cx(
					!!showThinkingIndicator
						? styles.stepContainerWithIndicator
						: styles.stepContainerWithoutIndicator,
				)}
			>
				{traces
					? traces.map((trace, i) => {
							const isLastItem = i === traces.length - 1;
							return (
								<ExpandableTrace
									key={`${trace.title}-${trace.status}`}
									trace={trace}
									isLastItem={isLastItem}
									placeholder={needsPlaceholder}
								/>
							);
						})
					: null}
			</Box>
		</Box>
	);
};

// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable-next-line no-barrel-files/no-barrel-files */
export { RovoThinkingIndicator } from './thinking-indicator';
